/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { FC, MouseEvent, useState } from "react";
import { Accordion } from "react-bootstrap";
import { useIntl } from "react-intl";
import { format } from "date-fns";

import "./TrackingItem.scss";
import { ReactComponent as ChevronDownIcon } from "assets/images/iconsV2/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "assets/images/iconsV2/chevron-up.svg";
import { ReactComponent as MarkerPinIcon } from "assets/images/iconsV2/marker-pin-01.svg";
import Dropdown from "components/v2/Dropdown/Dropdown";
import ConfirmationDialog from "components/organisms/ConfirmationDialog/ConfirmationDialog";
import Button from "components/v2/Button/Button";
import { updateTrackingReferences } from "api";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import { TrackingHistoryItemProps } from "../TrackingHistoryItem/TrackingHistoryItem";
import TrackingHistory from "../TrackingHistory/TrackingHistory";
import GroupedTrackingHistory, { GroupedTrackingHistoryItem } from "../GroupedTrackingHistory/GroupedTrackingHistory";
import TrackingException, { TrackingExceptionProps } from "../TrackingException/TrackingException";
import PortcastAirDetails from "../PortcastAirDetails/PortcastAirDetails";
import { TrackingMessageType, MessageComponentMapType } from "./TrackingItemTypes";
import TrackingLinkForDriver from "./TrackingLinkForDriver";
import RegenerateLinkModal from "./RegenerateLinkModal";

const trackingMessageComponentsMap: Record<keyof MessageComponentMapType, React.ComponentType<any>> = {
  trackingLinkForDriver: TrackingLinkForDriver,
  // Add other message components here as needed
};

type BaseTrackingItemProps = {
  /**
   * Unique identifier for each tracking item
   */
  uuid: string;
  /**
   * The ID of the tracking item.
   */
  id?: number;
  /**
   * The Type of the tracking item.
   */
  type?: string;
  /**
   * The shipment ID.
   */
  shipmentId?: string;
  /**
   * The label for the track ing item.
   * @example "Container number"
   */
  trackingLabel: string;
  /**
   * The tra cking number.
   * @example "FSDF4234235"
   */
  trackingNumber: string;
  /**
   * The suppor ting text for the tracking item.
   * Displayed below the tracking number.
   */
  supportingText?: string;
  /**
   * The delivery stop this tracking number is associated with.
   * Only set for multistop shipments where a stop was selected.
   */
  associatedStopLabel?: string;
  /**
   * When true, associated stop uses Figma copy: "Type - City, D MMM".
   */
  showAssociatedStopBelowLabel?: boolean;
  /**
   * The SCAC code.
   * @example "SDAL"
   */
  scacCode?: string;
  /**
   * The update message.
   * could be a string or a custom component.
   * @examp le "Container has been loaded onto the vessel."
   */
  message?: TrackingMessageType;
  /**
   * The date of the message.
   */
  date?: string;
  /**
   * The location o f the message.
   * @example "Sha nghai, China"
   */
  location?: string;
  /**
   * On edit click handler.
   */
  onEditClick?: () => void;
  /**
   * On edit click handler.
   */
  refreshTrackingData?: (type: string) => void;
  /**
   * Whether the tracking item is a booking reference
   */
  isBookingReference?: boolean;
  /**
   * if there are exceptions in the tracking it will contain props
   * to display the exception component.
   */
  exceptionProps?: TrackingExceptionProps;
  /**
   * Company shipper settings.
   */
  company_shipper_settings?: {
    [key: string]: any;
  };
};

export type TrackingItemWithHistoryProps = BaseTrackingItemProps & {
  /**
   * The history of the tracking item.
   */
  history: TrackingHistoryItemProps[] | GroupedTrackingHistoryItem[];
  /**
   * Whether the tracking item is expande d.
   */
  isExpanded: boolean;
  /**
   * Whether the history is grouped.
   */
  isGroupedHistory: boolean;
};

export type TrackingItemWithoutHistoryProps = BaseTrackingItemProps & {
  history?: undefined;
  isExpanded?: undefined;
  isGroupedHistory?: undefined;
};

export type TrackingItemProps = TrackingItemWithHistoryProps | TrackingItemWithoutHistoryProps;

const TrackingItem: FC<TrackingItemProps> = function (props) {
  const {
    id,
    uuid,
    type,
    shipmentId,
    trackingLabel,
    trackingNumber,
    supportingText,
    associatedStopLabel,
    showAssociatedStopBelowLabel = false,
    scacCode,
    message,
    date,
    location,
    isExpanded,
    history,
    isGroupedHistory,
    onEditClick,
    refreshTrackingData,
    isBookingReference,
    exceptionProps,
    company_shipper_settings,
  } = props;
  const intl = useIntl();
  const [isCustomBroker] = useCustomBrokerPermission();

  const [isHovered, setIsHovered] = useState(false); // Track hover state of this component
  const [isClickToCopyHovered, setIsClickToCopyHovered] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showRegenerateLinkModal, setShowRegenerateLinkModal] = useState<boolean>(false);

  // Click to copy functions
  const handleCopyTrackingNumber = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setHasCopied(true);
    navigator.clipboard.writeText(trackingNumber);
  };
  const handleMouseEnterCopy = () => setIsClickToCopyHovered(true);
  const handleMouseLeaveCopy = () => {
    setHasCopied(false);
    setIsClickToCopyHovered(false);
  };

  const handleDeleteClick = () => {
    setIsDeleting(true);
    const payload: any = {
      references: [
        {
          id,
          type,
          tracking_number: "",
        },
      ],
    };
    updateTrackingReferences(shipmentId, payload)
      .then(() => {
        if (refreshTrackingData && type) {
          refreshTrackingData(type);
        }
        setShowDeleteConfirmation(false);
      })
      .catch((err) => errorToast(err))
      .finally(() => {
        setIsDeleting(false);
      });
  };

  // Variables for rendering
  const clickToCopyText = hasCopied ? "Copied" : "Copy";
  const clickToCopyClass = hasCopied ? "tracking-item__click-to-copy--copied" : "tracking-item__click-to-copy--copy";
  const containerClass = `tracking-item__container ${isExpanded ? "tracking-item--expanded" : ""}`;
  const chevronIconClass = `tracking-item__chevron-icon ${isHovered ? "" : "ml-auto"}`;
  const kebabMenuClass = `tracking-item__kebab-menu ${history ? "" : "mr-md"}`;
  const messageClass = `tracking-item__message ${supportingText || location ? "tracking-item__message--truncated" : ""}`;
  const truckTrackingStatus =
    message && typeof message !== "string" && message.type === "trackingLinkForDriver" ? message.props.status : null;

  const dateWithoutOffset = date?.slice(0, 19).replace("T", " "); // Remove the timezone offset as we do not want to convert the date to the user's timezone
  const timezoneMatch = date?.match(/([+-]\d{2}:\d{2})$/);
  const timezoneOffset = timezoneMatch ? timezoneMatch[0] : null;

  const formattedDate = !dateWithoutOffset
    ? ""
    : location !== "Not available" || !timezoneOffset
    ? format(new Date(dateWithoutOffset), "dd MMM yyyy, HH:mm")
    : `${format(new Date(dateWithoutOffset), "dd MMM yyyy, HH:mm")} (UTC +${timezoneOffset})`;

  const shouldDisplayUpdateSection = date || message || location;
  const kebabMenuOptions = [
    {
      value: "edit",
      label: intl.formatMessage({ id: "general.edit", defaultMessage: "Edit" }),
      onClick: onEditClick || (() => {}),
    },
    ...(type === "truck" && company_shipper_settings?.truck_tracking_enabled && truckTrackingStatus !== "inactive"
      ? [
          {
            value: "regenerate_link",
            label: intl.formatMessage({ id: "tracking.truckRegenerateLink", defaultMessage: "Regenerate link" }),
            onClick: () => setShowRegenerateLinkModal(true),
          },
        ]
      : []),
    ...(!isBookingReference
      ? [
          {
            value: "delete",
            label: intl.formatMessage({ id: "general.delete", defaultMessage: "Delete" }),
            onClick: () => setShowDeleteConfirmation(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <Accordion.Toggle
        as="div"
        className={containerClass}
        eventKey={uuid}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="tracking-item__inner-container">
          <div className="tracking-item__info-container">
            <div className="tracking-item__number-container">
              <div
                className="tracking-item__click-to-copy"
                onMouseEnter={handleMouseEnterCopy}
                onMouseLeave={handleMouseLeaveCopy}
                onClick={handleCopyTrackingNumber}
              >
                <div className="tracking-item__label">
                  {trackingLabel}
                  {associatedStopLabel && (
                    <span
                      className="tracking-item__associated-stop"
                      data-testid={testIds.SHIPMENT_DASHBOARD_TRACKING_ITEM_ASSOCIATED_STOP}
                    >
                      {showAssociatedStopBelowLabel ? ` - ${associatedStopLabel}` : ` • ${associatedStopLabel}`}
                    </span>
                  )}{" "}
                  {isClickToCopyHovered && <span className={clickToCopyClass}>{clickToCopyText}</span>}
                </div>
                <div className="tracking-item__number-scac-container">
                  <div className="tracking-item__number">{trackingNumber}</div>
                  {scacCode && <div className="tracking-item__scac-code">• {scacCode}</div>}
                </div>
              </div>
              {supportingText && <div className="tracking-item__supporting-text">{supportingText}</div>}
            </div>
            {shouldDisplayUpdateSection && (
              <div className="tracking-item__update-container">
                {date && <div className="tracking-item__date">{formattedDate}</div>}
                {message && (
                  <div className={messageClass}>
                    {typeof message === "string"
                      ? message
                      : React.createElement(trackingMessageComponentsMap[message.type], message.props)}
                  </div>
                )}
                {location && (
                  <div className="tracking-item__location">
                    <div className="tracking-item__marker-pin-icon">
                      <MarkerPinIcon />
                    </div>
                    {location}
                  </div>
                )}
              </div>
            )}
            {isHovered && !isCustomBroker && (
              <div className={kebabMenuClass}>
                <Dropdown useMenuIcon alignRight options={kebabMenuOptions} />
              </div>
            )}
            {history && <div className={chevronIconClass}>{isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>}
          </div>
          {exceptionProps && <TrackingException {...exceptionProps} />}
        </div>
      </Accordion.Toggle>
      {history && (
        <Accordion.Collapse eventKey={uuid} className="tracking-item__children">
          <div className="p-3xl">
            <PortcastAirDetails history={history as GroupedTrackingHistoryItem[]} />
            {isGroupedHistory ? (
              <GroupedTrackingHistory history={history as GroupedTrackingHistoryItem[]} />
            ) : (
              <TrackingHistory history={history as TrackingHistoryItemProps[]} />
            )}
          </div>
        </Accordion.Collapse>
      )}
      <ConfirmationDialog
        title={intl.formatMessage({ id: "tracking.deleteTracking", defaultMessage: "Delete tracking?" })}
        description={intl.formatMessage({
          id: "tracking.deleteTrackingConfirmation",
          defaultMessage: "Are you sure you want to delete this tracking number?",
        })}
        dialogSize="sm"
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        customActions={
          <div className="d-flex align-items-center justify-content-end mt-lg">
            <div className="mr-lg">
              <Button label="Back" variant="secondary-gray" onClick={() => setShowDeleteConfirmation(false)} />
            </div>
            <Button
              label={
                isDeleting
                  ? intl.formatMessage({ id: "general.deleting", defaultMessage: "Deleting" })
                  : intl.formatMessage({ id: "general.delete", defaultMessage: "Delete" })
              }
              disabled={isDeleting}
              variant="destructive-primary"
              onClick={handleDeleteClick}
            />
          </div>
        }
      />
      <RegenerateLinkModal show={showRegenerateLinkModal} onHide={() => setShowRegenerateLinkModal(false)} trackingId={id} />
    </>
  );
};

export default TrackingItem;
